import {
  ArrowBackOutlined,
  ArrowForwardOutlined,
  AttachFileOutlined,
  CheckCircleOutlineOutlined,
  RestartAltOutlined,
  SaveOutlined,
  SendOutlined,
} from '@mui/icons-material'
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { useAppController } from 'customHooks/useAppController'
import { WorkingStepsFilter } from 'features/BillOfMaterials/BoMItemFilter/Components/WorkingStepsFilter/WorkingStepsFilter'
import { useBomItemThumbnailImageSrc } from 'features/BillOfMaterials/useBomItemThumbnailImageSrc'
import { useDialogManager } from 'features/DialogManager/useDialogManager'
import { ProjectSuppliersController } from 'features/ProjectSuppliers/ProjectSuppliersController'
import { BomItemPointer } from 'model/Project/BomItemPointer'
import { BomItemType } from 'model/Project/BoMItemRow'
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react'
import {
  ProjectSupplierDto,
  QuotationExchangeChannel,
  WorkingStepType,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import store from 'store/configureStore'
import { useShowException } from 'utils/useShowException'
import { ProjectRFQLines, RFQLine } from '../Components/ProjectRFQLines'
import {
  activityKey,
  preferredChannel,
  ProjectRFQController,
  SupplierAllocation,
} from '../ProjectRFQController'
import { parseQuantityBreaks } from './quantityBreaks'

export type DraftRFQDialogProps = { isOpen: boolean; onClose: () => void }
const steps = [
  'Scope',
  'Request details',
  'Quantity breaks',
  'Suppliers',
  'Review',
]

const supplierLabel = (supplier: ProjectSupplierDto) =>
  supplier.name || supplier.glNumber || 'Unnamed supplier'

const QuantityBreakItemThumbnail = ({
  pointer,
  itemName,
  rowNumber,
}: {
  pointer: BomItemPointer
  itemName: string
  rowNumber?: number
}) => {
  const { imageSrc } = useBomItemThumbnailImageSrc(pointer)

  return imageSrc ? (
    <Box
      component="img"
      src={imageSrc}
      alt={`${itemName} thumbnail`}
      sx={{
        width: 42,
        height: 42,
        flex: '0 0 auto',
        objectFit: 'contain',
        borderRadius: 1,
        bgcolor: 'background.default',
        border: 1,
        borderColor: 'divider',
      }}
    />
  ) : (
    <Box
      sx={{
        display: 'grid',
        placeItems: 'center',
        flex: '0 0 auto',
        width: 42,
        height: 42,
        borderRadius: 1,
        bgcolor: 'action.selected',
        color: 'text.secondary',
        fontSize: 12,
        fontWeight: 700,
      }}
    >
      {rowNumber ?? '-'}
    </Box>
  )
}

export const DraftRFQDialog = ({ isOpen, onClose }: DraftRFQDialogProps) => {
  const { controller, loading } = useAppController(
    () => new ProjectRFQController(),
  )
  const { controller: suppliersController } = useAppController(
    () => new ProjectSuppliersController(),
  )
  const showException = useShowException('request for quotation')
  const [activeStep, setActiveStep] = useState(0)
  const [lines, setLines] = useState<RFQLine[]>([])
  const [selectedWorkingSteps, setSelectedWorkingSteps] = useState<
    WorkingStepType[]
  >([])
  const [selectedBomItemIds, setSelectedBomItemIds] = useState<Set<string>>(
    new Set(),
  )
  const [title, setTitle] = useState('')
  const [buyerReference, setBuyerReference] = useState('')
  const [responseDeadline, setResponseDeadline] = useState('')
  const [requestedDeliveryDate, setRequestedDeliveryDate] = useState('')
  const [buyerMessage, setBuyerMessage] = useState('')
  const [attachments, setAttachments] = useState<File[]>([])
  const [bulkTiers, setBulkTiers] = useState('')
  const [bulkTierError, setBulkTierError] = useState('')
  const [quantityDrafts, setQuantityDrafts] = useState<Record<string, string>>(
    {},
  )
  const [quantityErrors, setQuantityErrors] = useState<Record<string, string>>(
    {},
  )
  const [suppliers, setSuppliers] = useState<ProjectSupplierDto[]>([])
  const [allocations, setAllocations] = useState<Record<string, string[]>>({})
  const [channels, setChannels] = useState<
    Record<string, QuotationExchangeChannel>
  >({})
  const [supplierLoading, setSupplierLoading] = useState(false)
  const [publishing, setPublishing] = useState(false)
  const [stepError, setStepError] = useState('')

  const selectedActivities = useMemo(
    () =>
      lines.flatMap((line) =>
        (line.activities ?? [])
          .filter((activity) => activity.selected)
          .map((activity) => ({
            line,
            activity,
            key: activityKey(line.bomItemPointer?.id, activity.activityId),
          })),
      ),
    [lines],
  )

  useEffect(() => {
    if (!isOpen || activeStep < 3 || suppliers.length > 0) return
    setSupplierLoading(true)
    suppliersController
      .getSuppliers()
      .then((response) => {
        const available = response.projectSuppliers ?? []
        setSuppliers(available)
        setChannels(
          Object.fromEntries(
            available.flatMap((supplier) =>
              supplier.supplierId
                ? [[supplier.supplierId, preferredChannel(supplier)]]
                : [],
            ),
          ),
        )
      })
      .catch(showException)
      .finally(() => setSupplierLoading(false))
  }, [
    activeStep,
    controller,
    isOpen,
    showException,
    suppliers.length,
    suppliersController,
  ])

  const applySelectedWorkingSteps = useCallback(() => {
    const project = store.getState().project
    if (!project) return
    const selected = new Set(selectedWorkingSteps)
    const next: RFQLine[] = []
    for (const assemblyId of project.filteredAssemblyHeaderIds) {
      const assembly = project.assemblyHeaders[assemblyId]
      const assemblyQuantity =
        assembly.financial?.totalProjectQuantity ?? assembly.financial?.quantity
      const assemblyActivities = assembly.assemblyActivities.filter(
        (activity) => selected.has(activity.primaryWorkingStep),
      )
      if (assemblyActivities.length) {
        next.push({
          bomItemPointer: { id: assemblyId, type: BomItemType.assemblyType },
          activities: assemblyActivities.map((activity) => ({
            activityId: activity.id,
            workingStepType: activity.primaryWorkingStep,
            selected: lines.some((line) =>
              line.activities?.some(
                (existing) =>
                  existing.activityId === activity.id && existing.selected,
              ),
            ),
            quantityBreaks:
              lines
                .flatMap((line) => line.activities ?? [])
                .find((existing) => existing.activityId === activity.id)
                ?.quantityBreaks ??
              (assemblyQuantity && assemblyQuantity > 0
                ? [assemblyQuantity]
                : undefined),
          })),
        })
      }
      for (const pointer of assembly.filteredPartTypePointers) {
        const part = project.partTypes[pointer.id]
        const partQuantity =
          part.financial?.totalProjectQuantity ?? part.financial?.quantity
        const activities = part.activities.filter((activity) =>
          selected.has(activity.primaryWorkingStep),
        )
        if (!activities.length) continue
        next.push({
          bomItemPointer: { id: pointer.id, type: BomItemType.partType },
          activities: activities.map((activity) => ({
            activityId: activity.id,
            workingStepType: activity.primaryWorkingStep,
            selected: lines.some((line) =>
              line.activities?.some(
                (existing) =>
                  existing.activityId === activity.id && existing.selected,
              ),
            ),
            quantityBreaks:
              lines
                .flatMap((line) => line.activities ?? [])
                .find((existing) => existing.activityId === activity.id)
                ?.quantityBreaks ??
              (partQuantity && partQuantity > 0 ? [partQuantity] : undefined),
          })),
        })
      }
    }
    setLines(next)
    setStepError(
      next.length
        ? ''
        : 'No activities match the selected working steps in the visible project scope.',
    )
  }, [lines, selectedWorkingSteps])

  const setActivitySelected = (
    bomItemId: string,
    index: number,
    selected: boolean,
  ) =>
    setLines((current) =>
      current.map((line) =>
        line.bomItemPointer?.id === bomItemId
          ? {
              ...line,
              activities: line.activities?.map((activity, i) =>
                i === index ? { ...activity, selected } : activity,
              ),
            }
          : line,
      ),
    )

  const setActivityTiers = (key: string, value: string) => {
    const parsed = parseQuantityBreaks(value)
    setQuantityErrors((current) => ({ ...current, [key]: parsed.error }))
    if (parsed.error) return
    setLines((current) =>
      current.map((line) => ({
        ...line,
        activities: line.activities?.map((activity) =>
          activityKey(line.bomItemPointer?.id, activity.activityId) === key
            ? { ...activity, quantityBreaks: parsed.quantities }
            : activity,
        ),
      })),
    )
    setQuantityDrafts((current) => ({
      ...current,
      [key]: parsed.quantities.join(', '),
    }))
  }

  const validateStep = (step: number) => {
    if (step === 0 && selectedActivities.length === 0)
      return 'Select at least one activity to quote.'
    if (step === 1 && !title.trim())
      return 'Enter a title so this request is recognizable.'
    if (step === 1 && !responseDeadline)
      return 'Choose when suppliers must respond.'
    if (step === 1 && new Date(responseDeadline) <= new Date())
      return 'The response deadline must be in the future.'
    if (
      step === 1 &&
      requestedDeliveryDate &&
      requestedDeliveryDate < responseDeadline
    )
      return 'Requested delivery must be on or after the response deadline.'
    if (
      step === 2 &&
      selectedActivities.some(
        ({ activity }) => !activity.quantityBreaks?.length,
      )
    )
      return 'Add at least one positive quantity break to every selected activity.'
    if (
      step === 3 &&
      selectedActivities.some(
        ({ key }) =>
          !Object.values(allocations).some((keys) => keys.includes(key)),
      )
    )
      return 'Assign every selected activity to at least one supplier.'
    return ''
  }

  const next = () => {
    const error = validateStep(activeStep)
    setStepError(error)
    if (!error) setActiveStep((step) => Math.min(step + 1, steps.length - 1))
  }

  const save = async (publish: boolean) => {
    for (let step = 0; step <= (publish ? 3 : 2); step += 1) {
      const error = validateStep(step)
      if (error) {
        setActiveStep(step)
        setStepError(error)
        return
      }
    }
    setPublishing(publish)
    try {
      const response = await controller.createRequestForQuotation(lines, {
        title: title.trim(),
        buyerReference: buyerReference.trim() || undefined,
        responseDeadline: new Date(responseDeadline),
        requestedDeliveryDate: requestedDeliveryDate
          ? new Date(requestedDeliveryDate)
          : undefined,
        buyerMessage: buyerMessage.trim() || undefined,
        attachments: [],
      })
      if (!response.success)
        throw new Error(
          response.errorMessage || 'The draft could not be saved.',
        )
      if (attachments.length) {
        await controller.uploadAttachments(response.rfqId, attachments)
      }
      if (publish) {
        const supplierAllocations: SupplierAllocation[] = suppliers.flatMap(
          (supplier) => {
            const supplierId = supplier.supplierId
            if (!supplierId || !allocations[supplierId]?.length) return []
            return [
              {
                supplier,
                activityKeys: allocations[supplierId],
                channel:
                  channels[supplierId] ?? QuotationExchangeChannel.Manual,
              },
            ]
          },
        )
        const outcomes = await controller.publishRequestForQuotation(
          response.rfqId,
          supplierAllocations,
        )
        const failed = outcomes.filter(
          (outcome) => outcome.result.status === 'rejected',
        )
        if (failed.length)
          throw new Error(
            `${failed.length} supplier invitation${failed.length === 1 ? '' : 's'} failed. The draft was saved; open Manage RFQs to retry.`,
          )
      }
      onClose()
    } catch (error) {
      showException(error)
    } finally {
      setPublishing(false)
    }
  }

  const renderScope = () => (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        height: '100%',
        flexDirection: { xs: 'column', md: 'row' },
      }}
    >
      <Box
        sx={{
          minWidth: { md: 230 },
          borderRight: { md: 1 },
          borderColor: 'divider',
          pr: { md: 2 },
        }}
      >
        <Typography variant="overline">Filter by working steps</Typography>
        <WorkingStepsFilter
          selectedFilters={selectedWorkingSteps}
          onFilterChange={(step) =>
            step &&
            setSelectedWorkingSteps((current) =>
              current.includes(step)
                ? current.filter((item) => item !== step)
                : [...current, step],
            )
          }
        />
        <Button
          fullWidth
          variant="contained"
          size="small"
          startIcon={<PlaylistAddIcon />}
          onClick={applySelectedWorkingSteps}
          disabled={!selectedWorkingSteps.length}
          sx={{ mt: 1 }}
        >
          Apply & load items
        </Button>
      </Box>
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {!lines.length ? (
          <Typography color="text.secondary" sx={{ p: 4, textAlign: 'center' }}>
            Choose working steps to discover quoteable activities.
          </Typography>
        ) : (
          <>
            <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
              <Button
                size="small"
                onClick={() =>
                  setLines((current) =>
                    current.map((line) => ({
                      ...line,
                      activities: line.activities?.map((activity) => ({
                        ...activity,
                        selected: true,
                      })),
                    })),
                  )
                }
              >
                Select all visible activities
              </Button>
              <Button
                size="small"
                color="inherit"
                onClick={() =>
                  setLines((current) =>
                    current.map((line) => ({
                      ...line,
                      activities: line.activities?.map((activity) => ({
                        ...activity,
                        selected: false,
                      })),
                    })),
                  )
                }
              >
                Clear selection
              </Button>
            </Stack>
            <ProjectRFQLines
              rfqLines={lines}
              onSelectionChange={(id, checked) =>
                setSelectedBomItemIds((current) => {
                  const next = new Set(current)
                  if (checked) next.add(id)
                  else next.delete(id)
                  return next
                })
              }
              selectedBomItemIds={selectedBomItemIds}
              onLineActivitySelectionChange={setActivitySelected}
              showSupplierAssignment={false}
            />
          </>
        )}
      </Box>
    </Box>
  )

  const renderDetails = () => (
    <Stack spacing={2} sx={{ maxWidth: 760, mx: 'auto' }}>
      <Typography variant="h6">
        Make the request easy to recognize and answer
      </Typography>
      <TextField
        required
        label="RFQ title"
        value={title}
        onChange={(event) => setTitle(event.target.value)}
        autoFocus
      />
      <TextField
        label="Buyer reference"
        value={buyerReference}
        onChange={(event) => setBuyerReference(event.target.value)}
        helperText="Optional internal or supplier-facing reference"
      />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          required
          fullWidth
          type="date"
          label="Response deadline"
          value={responseDeadline}
          onChange={(event) => setResponseDeadline(event.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          fullWidth
          type="date"
          label="Requested delivery date"
          value={requestedDeliveryDate}
          onChange={(event) => setRequestedDeliveryDate(event.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
        />
      </Stack>
      <TextField
        multiline
        minRows={4}
        label="Message to suppliers"
        value={buyerMessage}
        onChange={(event) => setBuyerMessage(event.target.value)}
      />
      <Button
        component="label"
        variant="outlined"
        startIcon={<AttachFileOutlined />}
        sx={{ alignSelf: 'flex-start' }}
      >
        Add attachments
        <input
          hidden
          multiple
          type="file"
          onChange={(event: ChangeEvent<HTMLInputElement>) =>
            setAttachments(Array.from(event.target.files ?? []))
          }
        />
      </Button>
      {!!attachments.length && (
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
          {attachments.map((file) => (
            <Chip
              key={`${file.name}-${file.size}`}
              label={file.name}
              onDelete={() =>
                setAttachments((current) =>
                  current.filter((item) => item !== file),
                )
              }
            />
          ))}
        </Stack>
      )}
    </Stack>
  )

  const renderQuantities = () => {
    const project = store.getState().project

    const applyBulkTiers = () => {
      const parsed = parseQuantityBreaks(bulkTiers)
      setBulkTierError(parsed.error)
      if (parsed.error) return

      const value = parsed.quantities.join(', ')
      setLines((current) =>
        current.map((line) => ({
          ...line,
          activities: line.activities?.map((activity) =>
            activity.selected
              ? { ...activity, quantityBreaks: parsed.quantities }
              : activity,
          ),
        })),
      )
      setQuantityDrafts((current) => ({
        ...current,
        ...Object.fromEntries(
          selectedActivities.map(({ key }) => [key, value]),
        ),
      }))
      setQuantityErrors((current) => ({
        ...current,
        ...Object.fromEntries(selectedActivities.map(({ key }) => [key, ''])),
      }))
    }

    return (
      <Stack spacing={1.5} sx={{ height: '100%', minHeight: 0 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Set requested quantities
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Every activity starts at its item quantity for the full project. Add
            price-break quantities where you want suppliers to quote
            alternatives.
          </Typography>
        </Box>

        <Paper
          variant="outlined"
          sx={{
            p: 1.5,
            bgcolor: 'action.hover',
            borderColor: 'primary.main',
          }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.5}
            sx={{ alignItems: { md: 'flex-start' } }}
          >
            <Box sx={{ minWidth: { md: 180 }, pt: { md: 0.5 } }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Apply to all {selectedActivities.length} activities
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Replaces every row below
              </Typography>
            </Box>
            <TextField
              fullWidth
              size="small"
              label="Quantity breaks"
              placeholder="10, 50, 100"
              value={bulkTiers}
              error={Boolean(bulkTierError)}
              helperText={
                bulkTierError ||
                'Separate quantities with commas, spaces, or semicolons'
              }
              onChange={(event) => {
                setBulkTiers(event.target.value)
                if (bulkTierError) setBulkTierError('')
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') applyBulkTiers()
              }}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">pcs</InputAdornment>
                  ),
                },
              }}
            />
            <Button
              variant="contained"
              onClick={applyBulkTiers}
              sx={{ minWidth: 120, height: 40, whiteSpace: 'nowrap' }}
            >
              Apply to all
            </Button>
          </Stack>
        </Paper>

        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{ flex: 1, minHeight: 240 }}
        >
          <Table stickyHeader size="small" sx={{ minWidth: 760 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '38%' }}>Item</TableCell>
                <TableCell sx={{ width: '22%' }}>Activity</TableCell>
                <TableCell align="right" sx={{ width: 120 }}>
                  Project qty.
                </TableCell>
                <TableCell sx={{ minWidth: 270 }}>
                  Requested quantities
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {selectedActivities.map(({ line, activity, key }) => {
                const pointer = line.bomItemPointer
                const item =
                  pointer?.type === BomItemType.assemblyType
                    ? project.assemblyHeaders?.[pointer.id]
                    : pointer?.type === BomItemType.partType
                      ? project.partTypes?.[pointer.id]
                      : undefined
                const itemName =
                  item?.name || item?.originalFileName || 'Unnamed BOM item'
                const itemQuantity =
                  item?.financial?.totalProjectQuantity ??
                  item?.financial?.quantity
                const draft =
                  quantityDrafts[key] ??
                  activity.quantityBreaks?.join(', ') ??
                  ''

                return (
                  <TableRow key={key} hover>
                    <TableCell>
                      <Stack
                        direction="row"
                        spacing={1.25}
                        sx={{ alignItems: 'center' }}
                      >
                        {pointer && (
                          <QuantityBreakItemThumbnail
                            pointer={pointer}
                            itemName={itemName}
                            rowNumber={item?.rowNumber}
                          />
                        )}
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600 }}
                            noWrap
                          >
                            {itemName}
                          </Typography>
                          <Tooltip
                            title={pointer?.id ?? ''}
                            placement="bottom-start"
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block', maxWidth: 300 }}
                              noWrap
                            >
                              {`${item?.rowNumber ? `#${item.rowNumber} · ` : ''}${
                                item?.drawingNumber
                                  ? `Drawing ${item.drawingNumber}${item.revision ? ` · Rev. ${item.revision}` : ''}`
                                  : pointer?.type === BomItemType.assemblyType
                                    ? 'Assembly'
                                    : 'Part'
                              }`}
                            </Typography>
                          </Tooltip>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color="primary"
                        variant="outlined"
                        label={activity.workingStepType || 'Unknown activity'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {itemQuantity ?? '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack
                        direction="row"
                        spacing={0.5}
                        sx={{ alignItems: 'flex-start' }}
                      >
                        <TextField
                          fullWidth
                          size="small"
                          value={draft}
                          error={Boolean(quantityErrors[key])}
                          helperText={quantityErrors[key] || undefined}
                          onChange={(event) => {
                            const value = event.target.value
                            setQuantityDrafts((current) => ({
                              ...current,
                              [key]: value,
                            }))
                            if (quantityErrors[key])
                              setQuantityErrors((current) => ({
                                ...current,
                                [key]: '',
                              }))
                          }}
                          onBlur={(event) =>
                            setActivityTiers(key, event.target.value)
                          }
                          onKeyDown={(event) => {
                            if (event.key === 'Enter')
                              event.currentTarget.blur()
                          }}
                          slotProps={{
                            htmlInput: {
                              'aria-label': `Requested quantities for ${itemName} ${activity.workingStepType ?? ''}`,
                            },
                          }}
                        />
                        <Tooltip title="Reset to project quantity">
                          <span>
                            <IconButton
                              size="small"
                              disabled={!itemQuantity}
                              aria-label={`Reset ${itemName} to project quantity`}
                              onClick={() => {
                                if (!itemQuantity) return
                                const value = String(itemQuantity)
                                setQuantityDrafts((current) => ({
                                  ...current,
                                  [key]: value,
                                }))
                                setActivityTiers(key, value)
                              }}
                            >
                              <RestartAltOutlined fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>
    )
  }

  const renderSuppliers = () => {
    if (supplierLoading)
      return (
        <Box sx={{ display: 'grid', placeItems: 'center', height: 240 }}>
          <CircularProgress aria-label="Loading suppliers" />
        </Box>
      )

    if (!suppliers.length)
      return (
        <Alert severity="warning">
          No eligible project suppliers are available. Add supplier capabilities
          and a quotation contact before publishing.
        </Alert>
      )

    const activitiesByType = new Map<
      WorkingStepType | undefined,
      typeof selectedActivities
    >()
    for (const selectedActivity of selectedActivities) {
      const type = selectedActivity.activity.workingStepType
      activitiesByType.set(type, [
        ...(activitiesByType.get(type) ?? []),
        selectedActivity,
      ])
    }

    return (
      <Stack spacing={1.5}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Choose suppliers for each activity
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Your BOM items and activities are already selected. Each choice
            below applies to every selected item with that activity.
          </Typography>
        </Box>

        {[...activitiesByType.entries()].map(([activityType, activities]) => {
          const activityKeys = activities.map(({ key }) => key)
          const compatibleSuppliers = suppliers.filter((supplier) =>
            supplier.activities?.some(
              (capability) => capability.workingStepType === activityType,
            ),
          )

          return (
            <Paper
              key={activityType ?? 'unknown'}
              variant="outlined"
              sx={{ p: 2 }}
            >
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                sx={{ justifyContent: 'space-between' }}
              >
                <Box sx={{ minWidth: { md: 240 } }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {activityType || 'Unknown activity'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {activities.length} selected item
                    {activities.length === 1 ? '' : 's'}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    flex: 1,
                    justifyContent: { md: 'flex-end' },
                    flexWrap: 'wrap',
                    gap: 1,
                  }}
                >
                  {compatibleSuppliers.length ? (
                    compatibleSuppliers.map((supplier) => {
                      const id = supplier.supplierId
                      if (!id) return null

                      const assignedCount = activityKeys.filter((key) =>
                        allocations[id]?.includes(key),
                      ).length
                      const allAssigned = assignedCount === activityKeys.length

                      return (
                        <FormControlLabel
                          key={id}
                          sx={{
                            m: 0,
                            px: 1,
                            border: 1,
                            borderColor: allAssigned
                              ? 'primary.main'
                              : 'divider',
                            borderRadius: 1,
                            bgcolor: allAssigned
                              ? 'action.selected'
                              : 'transparent',
                          }}
                          control={
                            <Checkbox
                              checked={allAssigned}
                              indeterminate={assignedCount > 0 && !allAssigned}
                              onChange={(_, checked) => {
                                const activityKeySet = new Set(activityKeys)
                                setAllocations((current) => ({
                                  ...current,
                                  [id]: checked
                                    ? [
                                        ...new Set([
                                          ...(current[id] ?? []),
                                          ...activityKeys,
                                        ]),
                                      ]
                                    : (current[id] ?? []).filter(
                                        (key) => !activityKeySet.has(key),
                                      ),
                                }))
                              }}
                            />
                          }
                          label={supplierLabel(supplier)}
                        />
                      )
                    })
                  ) : (
                    <Alert severity="warning" sx={{ py: 0 }}>
                      No compatible suppliers
                    </Alert>
                  )}
                </Box>
              </Stack>
            </Paper>
          )
        })}
      </Stack>
    )
  }

  const reviewAllocations = suppliers.filter(
    (supplier) =>
      supplier.supplierId && allocations[supplier.supplierId]?.length,
  )
  const renderReview = () => (
    <Stack spacing={2}>
      <Alert severity="info">
        Review the exact activity scope for each supplier before sending. Email
        delivery includes a secure supplier response link.
      </Alert>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="h6">{title}</Typography>
        <Typography color="text.secondary">
          {buyerReference || 'No buyer reference'} · responses due{' '}
          {responseDeadline}
        </Typography>
      </Paper>
      {reviewAllocations.map((supplier) => {
        const id = supplier.supplierId!
        const keys = allocations[id]
        return (
          <Paper key={id} variant="outlined" sx={{ p: 2 }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              sx={{ justifyContent: 'space-between', gap: 1 }}
            >
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {supplierLabel(supplier)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {keys.length} activit{keys.length === 1 ? 'y' : 'ies'}{' '}
                  included
                </Typography>
              </Box>
              <TextField
                select
                size="small"
                label="Delivery method"
                value={channels[id] ?? QuotationExchangeChannel.Manual}
                onChange={(event) =>
                  setChannels((current) => ({
                    ...current,
                    [id]: event.target.value as QuotationExchangeChannel,
                  }))
                }
                sx={{ minWidth: 220 }}
              >
                {(
                  supplier.configuredEndpoints?.map(
                    (endpoint) => endpoint.channel,
                  ) ?? [QuotationExchangeChannel.Manual]
                )
                  .filter((value, index, all) => all.indexOf(value) === index)
                  .map((channel) => (
                    <MenuItem key={channel} value={channel}>
                      {channel === QuotationExchangeChannel.Email
                        ? 'Email with secure response link'
                        : channel}
                    </MenuItem>
                  ))}
              </TextField>
            </Stack>
            <Divider sx={{ my: 1 }} />
            <Stack spacing={0.5}>
              {selectedActivities
                .filter(({ key }) => keys.includes(key))
                .map(({ line, activity, key }) => (
                  <Typography key={key} variant="body2">
                    <CheckCircleOutlineOutlined
                      color="success"
                      sx={{
                        fontSize: 16,
                        mr: 0.75,
                        verticalAlign: 'text-bottom',
                      }}
                    />
                    {activity.workingStepType} · BOM item{' '}
                    {line.bomItemPointer?.id} ·{' '}
                    {activity.quantityBreaks?.join(', ')}
                  </Typography>
                ))}
            </Stack>
          </Paper>
        )
      })}
    </Stack>
  )

  const content = [
    renderScope,
    renderDetails,
    renderQuantities,
    renderSuppliers,
    renderReview,
  ][activeStep]()
  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      slotProps={{ paper: { sx: { height: { xs: '100%', sm: '88vh' } } } }}
    >
      <DialogTitle>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          sx={{ justifyContent: 'space-between', gap: 1 }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Draft Request for Quotation
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Activities are the quotation unit. BOM items provide project
              context.
            </Typography>
          </Box>
          <Chip
            color="primary"
            variant="outlined"
            label={`${selectedActivities.length} selected activit${selectedActivities.length === 1 ? 'y' : 'ies'}`}
          />
        </Stack>
      </DialogTitle>
      <Stepper activeStep={activeStep} alternativeLabel sx={{ px: 3, pb: 2 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      <Divider />
      <DialogContent sx={{ py: 2 }}>
        {stepError && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            onClose={() => setStepError('')}
          >
            {stepError}
          </Alert>
        )}
        {content}
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 1.5, justifyContent: 'space-between' }}>
        <Button
          onClick={
            activeStep
              ? () => {
                  setStepError('')
                  setActiveStep((step) => step - 1)
                }
              : onClose
          }
          startIcon={activeStep ? <ArrowBackOutlined /> : undefined}
        >
          {activeStep ? 'Back' : 'Cancel'}
        </Button>
        <Stack direction="row" spacing={1}>
          {activeStep >= 2 && (
            <Button
              startIcon={<SaveOutlined />}
              disabled={
                Boolean(loading.CreateRequestForQuotation) || publishing
              }
              onClick={() => save(false)}
            >
              Save draft
            </Button>
          )}
          {activeStep < steps.length - 1 ? (
            <Button
              variant="contained"
              endIcon={<ArrowForwardOutlined />}
              onClick={next}
            >
              Continue
            </Button>
          ) : (
            <Button
              variant="contained"
              startIcon={
                publishing ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <SendOutlined />
                )
              }
              disabled={publishing || !reviewAllocations.length}
              onClick={() => save(true)}
            >
              Publish & send
            </Button>
          )}
        </Stack>
      </DialogActions>
    </Dialog>
  )
}

export const OpenRFQDraftDialogButton = () => {
  const { openDialog } = useDialogManager()
  return <Button onClick={() => openDialog('DraftRFQDialog')}>Draft RFQ</Button>
}
